import { useEffect, useMemo, useState } from 'react';
import {
  firstTalkPending,
  guardianIncidents,
  guardianIncidentsNetwork,
  isOpenEmergency,
  sortEmergencies,
  type GuardianIncident,
  residentAck,
  residentAssign,
  residentFirstTalkDone,
  residentObserve,
  residentsHandoff,
  residentsWatch,
  watchCaregivers,
  type ApiError,
  type CaregiversResponse,
  type FirstTalkPendingResponse,
  type WatchMovementResponse,
} from '@aithora/shared';
import { ErrorState, Skeleton } from '../../ui/states';
import { type ResidentsCtx, useApi, useNow, useWidth } from './common';
import { pre } from './dom';
import { watchVals, type WatchUi } from './watchVals';
import { WatchListView } from './WatchListView';
import { TonightView } from './TonightView';
import { tonightVals, TONIGHT_NOT_CONNECTED, type TonightKey } from './tonightVals';
import { ChartPane } from './ChartPane';
import { facilitiesParam, useFacilityScope, usePickedIds } from '../../session/FacilityScope';
import { sx } from '../../ui/designStyles';

/** V9 L2206-2213 (resNoneOpen): no residents connected for the buildings in view yet. */
export function ResidentsNotConnected({ where }: { where: string }) {
  return (
    <div data-screen-label="Residents" style={sx('display:flex;flex-direction:column;gap:8px;max-width:640px;padding:20px 22px;border-radius:12px;background:#FCFAF4;border:1px solid rgba(34,39,46,.12);')}>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;padding:3px 7px;border-radius:4px;color:#54606C;background:rgba(84,96,108,.12);')}>NOT CONNECTED</span>
        <span style={sx('font-size:16px;color:#54606C;')}><span>{where}</span></span>
      </div>
      <div style={sx('font-size:18px;font-weight:700;color:#22272E;')}>No residents connected for this building yet</div>
      <div style={sx('font-size:16px;line-height:1.5;color:#54606C;text-wrap:pretty;')}>Residents appear here once the building’s census feed is live on Aithora. Nothing is shown until then.</div>
    </div>
  );
}

/** How often the watch list re-reads the chip feed (the design's LIVE line). */
export const WATCH_POLL_MS = 60_000;
/** How often the list re-reads open emergencies (the same pace as the emergency line). */
export const EMERGENCY_ROW_POLL_MS = 15_000;

/** An emergency's kind in a word for its row ("Fall · now"). */
export function emergencyWord(type: string | null | undefined): string {
  const t = String(type ?? '').toUpperCase();
  if (t === 'FALL' || t === 'FALL_DETECTED') return 'Fall';
  return 'Emergency';
}

/**
 * The residents who are an open emergency right now, and whether someone has claimed each
 * (design rwLayer: "Fall · now · nobody has claimed it yet"), worst first.
 */
export function liveEmergencies(incidents: readonly GuardianIncident[]): Array<{ residentId: string; kind: string; claimed: boolean }> {
  return sortEmergencies(incidents.filter((i) => isOpenEmergency(i) && !!i.user_id)).map((i) => ({
    residentId: String(i.user_id),
    kind: emergencyWord(i.type),
    claimed: !!i.acknowledged_at,
  }));
}

/**
 * The Residents tab: the watch list (design rwList), and the chart behind "Chart ->"
 * (design rwChart). Aithora reads it across buildings, read only.
 */
export function RosterTab({ ctx, focus, setFocus, setSubject }: { ctx: ResidentsCtx; focus: string | null; setFocus: (id: string | null) => void; setSubject?: (name: string | null) => void }) {
  const [open, setOpen] = useState<boolean>(!!focus);
  const [sel, setSel] = useState<string | null>(focus);
  const [chartTab, setChartTab] = useState<'overview' | 'vitals' | 'measures' | 'chart'>('overview');
  const pick = facilitiesParam(usePickedIds());
  const { inView } = useFacilityScope();
  const watch = useApi<WatchMovementResponse>((c, signal) => residentsWatch(c, { query: { window: '7d', ...(pick ? { facilities: pick } : {}) }, signal }), [pick], { pollMs: WATCH_POLL_MS });
  const isAdmin = ctx.persona === 'Facility Admin';
  // Who is an emergency right now: a building's staff read their own building; Aithora's team
  // reads the buildings in view. The Owner/Operator has no Guardian (the server refuses it).
  const team = ctx.persona === 'Operator';
  const incidents = useApi<{ incidents: GuardianIncident[] } | null>(
    (c, signal) => (team
      ? guardianIncidentsNetwork(c, { query: pick ? { facilities: pick } : undefined, signal }).catch(() => null)
      : guardianIncidents(c, { signal }).catch(() => null)),
    [pick, team],
    { pollMs: EMERGENCY_ROW_POLL_MS, enabled: ctx.role !== 'PARTNER' },
  );
  const emergencies = useMemo(() => liveEmergencies(incidents.data?.incidents ?? []), [incidents.data]);
  const [tonightOpen, setTonightOpen] = useState<TonightKey | null>(null);
  // The selected resident (the first on the list until one is picked) names the Atlas button.
  const subjectId = sel ?? watch.data?.residents[0]?.residentId ?? null;
  const subjectName = watch.data?.residents.find((r) => r.residentId === subjectId)?.name ?? null;
  useEffect(() => { if (setSubject && subjectName) setSubject(subjectName); }, [subjectName, setSubject]);
  const caregivers = useApi<CaregiversResponse>((c, signal) => watchCaregivers(c, { signal }), [], { enabled: isAdmin });
  const firstTalk = useApi<FirstTalkPendingResponse>((c, signal) => firstTalkPending(c, { signal }), [], { enabled: ctx.persona !== 'Operator' });
  const { width, phone } = useWidth();
  const nowMs = useNow();
  const [ui, setUiState] = useState<WatchUi>({ sort: 'moved', view: 'today', query: '', pick: null, quietOpen: false, hoOpen: false, hoSent: null });
  const [suOpen, setSuOpen] = useState(false);
  const [writeError, setWriteError] = useState<ApiError | null>(null);
  const setUi = (p: Partial<WatchUi>) => setUiState((s) => ({ ...s, ...p }));
  const canAct = !ctx.readOnly && ctx.persona !== 'Operator' && !(watch.data?.readOnly ?? false);

  const run = (p: Promise<unknown>, reload = true) => {
    setWriteError(null);
    p.then(() => { if (reload) watch.reload(); }).catch((e: ApiError) => setWriteError(e));
  };

  const vals = useMemo(() => {
    if (!watch.data) return null;
    return watchVals({
      rows: watch.data.residents,
      canAct,
      isAdmin,
      roleLabel: ctx.roleLabel,
      phone,
      width,
      nowMs,
      viewerId: ctx.viewerId,
      lastShiftEnd: watch.data.lastShiftEnd,
      caregivers: (caregivers.data?.caregivers ?? []).map((c) => ({ userId: c.userId, name: c.name })),
      team,
      emergencies,
      ui,
      act: {
        setUi,
        open: (id, tab) => { setSel(id); setFocus(null); setOpen(true); if (tab) setChartTab(tab); },
        ack: (id) => run(residentAck(ctx.client, { params: { id }, body: {} })),
        notice: (id, what) => run(residentObserve(ctx.client, { params: { id }, body: { what } })),
        assign: (id, caregiverId) => run(residentAssign(ctx.client, { params: { id }, body: caregiverId ? { caregiverId } : {} })),
        handoff: (lines) => {
          setWriteError(null);
          residentsHandoff(ctx.client, { body: { lines } })
            .then(() => setUi({ hoSent: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) }))
            .catch((e: ApiError) => setWriteError(e));
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch.data, caregivers.data, ui, width, phone, canAct, isAdmin, nowMs, team, emergencies]);

  // Atlas first-talk line (design rwSu*, L41267): Care Staff and the Facility Admin.
  const su = useMemo(() => {
    if (ctx.persona === 'Operator' || !firstTalk.data) return { rwSuShow: false };
    const list = firstTalk.data.residents;
    const todo = list.filter((r) => !r.completedAt);
    const done = list.filter((r) => r.completedAt);
    const hhmm = (iso: string) => new Date(iso).toTimeString().slice(0, 5);
    return {
      rwSuShow: true,
      rwSuHasTodo: todo.length > 0 && canAct,
      rwSuOpen: suOpen && todo.length > 0,
      rwSuToggle: (e?: { preventDefault?: () => void }) => { pre(e); setSuOpen(!suOpen); },
      rwSuNote: (todo.length ? todo.length + ' still to do with you' : 'all done') + (done.length ? ' · ' + done.map((r) => '✓ ' + String(r.name ?? '').split(' ')[0] + ' ' + hhmm(r.completedAt!)).join(', ') : ''),
      rwSuList: todo.map((r) => ({
        label: r.name ?? 'Resident',
        onClick: (e?: { preventDefault?: () => void }) => {
          pre(e);
          setSuOpen(false);
          residentFirstTalkDone(ctx.client, { params: { id: r.residentId }, body: {} }).then(() => firstTalk.reload()).catch((er: ApiError) => setWriteError(er));
        },
      })),
    };
  }, [firstTalk.data, suOpen, ctx, canAct]);

  if (open && (sel || watch.data?.residents[0])) {
    return (
      <ChartPane
        ctx={ctx}
        rows={watch.data?.residents ?? []}
        watch={watch.data}
        selId={sel ?? watch.data!.residents[0].residentId}
        focusMissing={focus && watch.data && !watch.data.residents.some((r) => r.residentId === focus) ? focus : null}
        onSelect={(id) => { setSel(id); setFocus(null); }}
        onBack={() => setOpen(false)}
        clearFocus={() => setFocus(null)}
        tab={chartTab}
        setTab={setChartTab}
        query={ui.query}
        setQuery={(v) => setUi({ query: v })}
      />
    );
  }
  if (watch.loading && !watch.data && !watch.error) return <Skeleton rows={8} label="Loading the watch list" />;
  if (watch.error && !watch.data) return <ErrorState error={watch.error} onRetry={watch.reload} />;
  if (!vals) return null;
  const V = {
    ...vals,
    ...su,
    rwList: true,
    rQuery: ui.query,
    onRQuery: (e: { target: { value: string } }) => setUi({ query: e.target.value }),
  };
  // V9 resNone: the Owner/Operator with no resident connected in scope sees why, not an empty list.
  if (ctx.role === 'PARTNER' && watch.data && watch.data.residents.length === 0) {
    return <ResidentsNotConnected where={inView.length === 1 ? inView[0].name : 'your buildings'} />;
  }
  const T = tonightVals({
    persona: ctx.persona,
    shown: ctx.role !== 'PARTNER',
    rows: watch.data?.residents ?? [],
    feeds: TONIGHT_NOT_CONNECTED,
    nowMs,
    open: tonightOpen,
    setOpen: setTonightOpen,
  });
  return (
    <>
      <TonightView V={T} />
      <WatchListView V={V} />
      {writeError && <ErrorState error={writeError} />}
    </>
  );
}
